import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { App } from "./App";
import { FuwaloHome } from "./pages/FuwaloHome";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { MultiplayerPianoAppArticle } from "./pages/articles/MultiplayerPianoAppArticle";
import { FallingMidiMatchArticle } from "./pages/articles/FallingMidiMatchArticle";
import { PianoDuetsWithFriendsArticle } from "./pages/articles/PianoDuetsWithFriendsArticle";
import { LearnPianoWithFriendsArticle } from "./pages/articles/LearnPianoWithFriendsArticle";
import "./styles.css";

/* -----------------------------------------------------------------------
 * Error Boundary — prevents white-screen crashes from reaching the user
 * ----------------------------------------------------------------------- */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Error is intentionally NOT logged to the console in production
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            background: "#fafafa",
            padding: "24px",
          }}
        >
          <div
            style={{
              maxWidth: 440,
              textAlign: "center",
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.06)",
              borderRadius: 16,
              padding: "48px 32px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                margin: "0 auto 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #fef3c7, #fffbeb)",
                borderRadius: 12,
                color: "#d97706",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
            </div>
            <h1
              style={{
                margin: "0 0 8px",
                fontSize: "1.25rem",
                fontWeight: 700,
                color: "#1a1a1a",
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                margin: "0 0 24px",
                fontSize: "0.9375rem",
                color: "#5c5c5c",
                lineHeight: 1.5,
              }}
            >
              We ran into an unexpected issue. Please refresh the page to try
              again.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "12px 28px",
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: "#fff",
                background: "#2d2d2d",
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
              }}
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* -----------------------------------------------------------------------
 * App bootstrap
 * ----------------------------------------------------------------------- */
const container = document.getElementById("root");

if (!container) {
  // Silently fail for end-users; only relevant during development
  document.body.textContent = "";
}

const root = ReactDOM.createRoot(container!);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<FuwaloHome />} />
          <Route path="/star" element={<App />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/multiplayer-piano-app" element={<MultiplayerPianoAppArticle />} />
          <Route path="/falling-midi-match" element={<FallingMidiMatchArticle />} />
          <Route path="/piano-duets-with-friends" element={<PianoDuetsWithFriendsArticle />} />
          <Route path="/learn-piano-with-friends" element={<LearnPianoWithFriendsArticle />} />
          {/* Catch-all: redirect unknown routes to the homepage */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

